import { useEffect, useRef, useState } from "react";

import { toastManager } from "../ui/toast";
import type { ProjectExecutionStatus } from "./projectExecutionStatus";

/**
 * Whether this session may look at a CMMD project's pages.
 *
 * `pending` is not "maybe" — it is "the answer is not knowable yet", and the
 * distinction is load-bearing. `useCmmdProjectHome` initialises
 * `executionStatus` to `no_repo` with `loading` true, so a gate that reads the
 * status before the fetch resolves evicts every project, including the ones it
 * is supposed to admit.
 */
export type ProjectAccess = "pending" | "allowed" | "blocked";

export interface ProjectAccessInput {
  readonly loading: boolean;
  readonly executionStatus: ProjectExecutionStatus;
}

/**
 * Forge's project pages describe a Forge project. A CMMD project with no Forge
 * project has nothing for them to describe, so they are not shown for one.
 *
 * Two deliberate exceptions to "blocked unless linked":
 *
 * - **`error` is allowed through.** That status means the repository fetch
 *   failed, i.e. we could not tell — and bouncing a real project because one
 *   request 500'd turns a transient into an eviction. The page carries an error
 *   state of its own; let it say so.
 * - **The verdict latches.** Only the first resolved answer counts. Deleting the
 *   Forge project flips `linked` to `repo_ready` on the next load, and the page
 *   is already navigating home by then; re-deciding here would race that and
 *   fire "this project isn't set up in Forge" over a successful delete.
 */
export function nextProjectAccess(
  previous: ProjectAccess,
  input: ProjectAccessInput,
): ProjectAccess {
  if (previous !== "pending") return previous;
  if (input.loading) return "pending";
  return input.executionStatus === "linked" || input.executionStatus === "error"
    ? "allowed"
    : "blocked";
}

/**
 * Holds the access verdict for a project page and leaves once it is `blocked`.
 *
 * A hook rather than three statements inline in `CmmdProjectHome`, which is
 * already a 190-line component carrying a dozen hooks — the state and both
 * effects belong to one decision, so they move together and the page just asks
 * whether it may render.
 */
export function useProjectAccessGate(input: {
  readonly loading: boolean;
  readonly executionStatus: ProjectExecutionStatus;
  readonly onBlocked: () => void;
}): ProjectAccess {
  const { loading, executionStatus, onBlocked } = input;
  const [access, setAccess] = useState<ProjectAccess>("pending");
  // Read through a ref so the leave effect depends on the verdict alone. The
  // tasks route passes `onBlocked` as an inline arrow, a new identity on every
  // render; with it in the dependency list the effect re-ran on each render
  // while blocked, adding one more notice per intake refetch (forge#3491).
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;

  useEffect(() => {
    setAccess((previous) => nextProjectAccess(previous, { loading, executionStatus }));
  }, [loading, executionStatus]);

  useEffect(() => {
    if (access !== "blocked") return;
    toastManager.add({
      type: "error",
      title: "Project isn't set up in Forge",
      description: "Import or link it to a Forge project to open it here.",
    });
    onBlockedRef.current();
  }, [access]);

  return access;
}
